/* 
  NOTE: While it's relatively easy to style a native <dialog> in Chrome/Safari (with a few caveats), Firefox is much much much more difficult. 
  
  For example, if the <dialog> is ever positioned, suddenly CSS transforms that exist in any **parent element** of the <dialog> can actually affect the styling of the <dialog>, even though:
  
  1. The <dialog> element when opened in "modal" mode exists specifically to break out of parent rendering contexts and should prevent this from happening (so the behavior is likely a bug in Firefox).
  2. The <dialog> exists inside the shadow DOM of the <aha-dialog>, and as such styles like this should not be able to cross the shadow DOM barrier. Again, this should be impossible, but here we are. 
  
  These transforms on any parent element can cause elements inside the <dialog> to be mis-aligned, scrolled off screen, invisible, or even break the ability to interact with them. In short, Firefox is a mess. 
  
  Hopefully one day Firefox will fix these bugs, but for now, the styling compromises in this file are a necessary fact of life if we want to use the native <dialog> element.
 */

:host {
  --aha-dialog--shadow: var(--theme-shadow-medium);
  --aha-dialog--shadow-maximal: var(--theme-shadow-deep);
  --aha-dialog--filter: saturate(0);
  --aha-dialog--filter-maximal: saturate(1);
  --aha-dialog--fade-duration: 0.2s;
  --aha-dialog--fade-timing-function: cubic-bezier(0.39, 0.58, 0.57, 1);
  --aha-dialog--fade-in-duration: var(--aha-dialog--fade-duration);
  --aha-dialog--fade-in-timing-function: var(--aha-dialog--fade-timing-function);
  --aha-dialog--fade-out-duration: 0.1s;
  --aha-dialog--fade-out-timing-function: var(--aha-dialog--fade-timing-function);
  --aha-dialog--maximal-fade-duration: 0.1s;
  --aha-dialog--loader-fade-duration: 0.1s;
  --aha-dialog--background: var(--theme-primary-background);

  --aha-dialog--min-width: ;
  --aha-dialog--width: ;
  --aha-dialog--max-width: ;
  --aha-dialog--min-height: ;
  --aha-dialog--height: ;
  --aha-dialog--max-height: ;

  z-index: -1;
  display: contents;
}
:host,
* {
  box-sizing: border-box;
}

dialog {
  padding: 0;
  border: none;
  z-index: 0;
  background: none;
  outline: none;
  height: 100vh;
  width: 100vw;
  margin: 0;
  max-width: 100%;
  max-height: 100%;
}

dialog::backdrop {
  /* NOTE the ::backdrop pseudo-element "neither inherits from nor is inherited by any other elements" from https://developer.mozilla.org/en-US/docs/Web/CSS/::backdrop ... this means that no CSS variables can be used within ::backdrop unless they are specified directly within ::backdrop itself, rendering CSS variables effectively useless in this scenario. */
  background: rgba(51, 51, 51, 0.2);
}
/* Ensure that only the topmost <dialog> which can have a backdrop (ie. does not have `hideBackdrop === true`) actually _has_ a backdrop. */
:host(:not([data-backdrop])) dialog::backdrop {
  display: none;
}

#boundary {
  width: 100%;
  height: 100%;
}

/* Center vertically and horizontally. */
:host([position='center']) #inner {
  inset-block: 0;
  inset-inline: 0;
}

/* Center horizontally. */
:host([position='h-center']) #inner {
  inset-inline: 0;
}

/* Center vertically. */
:host([position='v-center']) #inner {
  inset-block: 0;
}

:host(
    :is([position='center'], [position='h-center'], [position='v-center']):is(
        [style^='top'],
        [style^='bottom'],
        [style*=';top'],
        [style*=';bottom'],
        [style*=' top'],
        [style*=' bottom'],
        [style^='inset'],
        [style*=';inset'],
        [style*=' inset']
      )
  )
  #inner {
  inset-block: auto;
  height: auto;
}

:host(
    :is([position='center'], [position='h-center'], [position='v-center']):is(
        [style^='left'],
        [style^='right'],
        [style*=';left'],
        [style*=';right'],
        [style*=' left'],
        [style*=' right'],
        [style^='inset'],
        [style*=';inset'],
        [style*=' inset']
      )
  )
  #inner {
  inset-inline: auto;
  width: auto;
}

#inner {
  display: inline-block;
  position: absolute;
  background: var(--aha-dialog--background);
  min-width: var(--aha-dialog--min-width);
  width: var(--aha-dialog--width, fit-content);
  max-width: var(--aha-dialog--max-width);
  min-height: var(--aha-dialog--min-height);
  height: var(--aha-dialog--height, fit-content);
  max-height: var(--aha-dialog--max-height);
  box-shadow: var(--aha-dialog--shadow);
  filter: var(--aha-dialog--filter);
  margin: auto;
  transition: box-shadow 0.2s linear;
}

#inner.closing {
  animation: var(--aha-dialog--fade-out-duration) linear fadeOut forwards;
}
#inner.opening {
  animation: var(--aha-dialog--fade-in-duration) linear fadeIn forwards;
}
:host([maximal]) #inner {
  box-shadow: var(--aha-dialog--shadow-maximal);
  filter: var(--aha-dialog--filter-maximal);
}

/* Loader specific CSS. */
#loader {
  display: none;
  position: absolute;
  inset: 0;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity var(--aha-dialog--loader-fade-duration) ease-in-out;
}
:host([loading]) #loader {
  opacity: 1;
  display: flex;
}
:host ::slotted(*) {
  transition: filter var(--aha-dialog--maximal-fade-duration) ease-in-out;
}
:host([loading]) ::slotted(*) {
  filter: grayscale(1) opacity(0.5);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    display: none;
  }
}

@media (prefers-reduced-motion) {
  :host {
    --aha-dialog--fade-in-duration: 0s;
    --aha-dialog--fade-out-duration: 0s;
    --aha-dialog--maximal-fade-duration: 0s;
    --aha-dialog--loader-fade-duration: 0s;
  }
}

@media (prefers-contrast: more) {
  #inner {
    outline: var(--outline-focus-ring);
  }
}
